import { serverFetch as fetchSearchFacets } from '~~/modules/api/rurality/v3/search/seo/facet'
import { browserFetch as fetchFindCottages } from '~~/modules/api/rurality/v3/search/cottages'
import type { schemaType } from '~~/modules/api/rurality/v3/search/cottages/schema'
import type { schemaType as FacetsSchemaType } from '~~/modules/api/rurality/v3/search/seo/facet/schema'
import type { paramsTypeAllRequired } from '~~/modules/api/rurality/v3/search/cottages/paramsSchema'
import type { MetaSuccessRuralitySchemaType } from '~~/modules/api/utils/rurality/v3/fetch/ruralitySchema'
import { useCottageListFilters } from '~~/modules/views/cottage-list/composables/useCottageListFilters'
import decoratorCottageListToMenuBreadcrumb from '~~/modules/views/cottage-list/utils/decoratorCottageListToMenuBreadcrumb'
import type { MenuBreadcrumbItems } from '~~/modules/menu/entities/MenuBreadcrumbItems'
import type { HeadInfo } from '~~/modules/head/entities/HeadInfo'

export interface CottageListPageOptions {
  slug?: string
  corner?: string
}

export interface BreadcrumbData {
  name: string
  middleItems?: MenuBreadcrumbItems
}

export interface CottageListPageReturn {
  pending: Ref<boolean>
  error: Ref<ApiResponseError | null>
  cottagesData: Ref<schemaType | undefined>
  cottagesMeta: Ref<MetaSuccessRuralitySchemaType | undefined>
  facetsData: Ref<FacetsSchemaType | undefined>
  getCurrentLocationName: () => string
  fetchCottagesData: (params: paramsTypeAllRequired) => Promise<void>
  initializePage: () => Promise<void>
  getBreadcrumbData: () => BreadcrumbData | null
  getHeadInfoData: (robotsVisible?: boolean) => HeadInfo
  showErrorPage: (errorObj: ApiResponseError) => void
}

export function useCottageListPage(
  options: CottageListPageOptions,
): CottageListPageReturn {
  const { parseUrlParams, hasFiltersApplied } = useCottageListFilters()
  const initialParams = parseUrlParams()
  const pending = ref<boolean>(false)
  const error = ref<ApiResponseError | null>(null)
  const cottagesData = ref<schemaType | undefined>(undefined)
  const cottagesMeta = ref<MetaSuccessRuralitySchemaType | undefined>(undefined)
  const facetsData = ref<FacetsSchemaType | undefined>(undefined)

  const route = useRoute()
  const routePath = route.path

  let facetSlug: string | undefined

  if (routePath.startsWith('/campings')) {
    facetSlug = 'campings'
  } else if (routePath.startsWith('/casas-rurales-')) {
    const pathSegments = routePath.split('/')
    facetSlug = pathSegments[1]
  } else if (routePath.startsWith('/hoteles-rurales-')) {
    const slug = route.params.slug as string
    facetSlug = `hoteles-rurales-${slug}`
  }

  const isFacetsMode = !!facetSlug
  const nuxtApp = useNuxtApp()

  const fetchCottagesData = async (params: paramsTypeAllRequired) => {
    pending.value = true
    error.value = null

    const data = await nuxtApp.runWithContext(() => fetchFindCottages(params))

    if (data.success && data.success.data) {
      pending.value = false
      cottagesData.value = data.success.data
      cottagesMeta.value = data.success.meta
    } else if (data.error) {
      pending.value = false
      error.value = data.error
    }
  }

  async function initializePage() {
    pending.value = true
    error.value = null

    if (isFacetsMode) {
      const facetsResponse = await fetchSearchFacets({
        facetSlug: facetSlug!,
      })

      if (facetsResponse.error) {
        pending.value = false
        error.value = facetsResponse.error
        return
      }

      if (facetsResponse.success && facetsResponse.success.data) {
        facetsData.value = facetsResponse.success.data

        const queryParams = new URLSearchParams(
          facetsResponse.success.data.query,
        )

        const fetchParams: paramsTypeAllRequired = {
          page: initialParams.page,
          l: queryParams.get('l') || initialParams.l,
          f: queryParams.get('f') || initialParams.f,
          min_price: queryParams.get('min_price') || initialParams.min_price,
          max_price: queryParams.get('max_price') || initialParams.max_price,
          d: queryParams.get('d') || initialParams.d,
          c: queryParams.get('c') || initialParams.c,
          a: queryParams.get('a') || initialParams.a,
          cor: queryParams.get('cor') || initialParams.cor,
          ec: queryParams.get('ec') || initialParams.ec,
          r: queryParams.get('r') || initialParams.r,
          bo: queryParams.get('bo') || initialParams.bo,
          of: queryParams.get('of') || initialParams.of,
          sort: queryParams.get('sort') || initialParams.sort,
          q: queryParams.get('q') || initialParams.q,
          ne: queryParams.get('ne') || initialParams.ne,
          sw: queryParams.get('sw') || initialParams.sw,
        }

        await fetchCottagesData(fetchParams)
      }
    } else {
      const fetchParams: paramsTypeAllRequired = {
        page: initialParams.page,
        l: options.slug || initialParams.l,
        f: initialParams.f,
        min_price: initialParams.min_price,
        max_price: initialParams.max_price,
        d: initialParams.d,
        c: initialParams.c,
        a: initialParams.a,
        cor: options.corner || initialParams.cor,
        ec: initialParams.ec,
        r: initialParams.r,
        bo: initialParams.bo,
        of: initialParams.of,
        sort: initialParams.sort,
        q: initialParams.q,
        ne: initialParams.ne,
        sw: initialParams.sw,
      }

      await fetchCottagesData(fetchParams)
    }
  }

  const isCornerPage = computed(() => {
    return !!cottagesData.value?.corner
  })

  function getBreadcrumbData(): BreadcrumbData | null {
    if (!cottagesData.value?.breadCrumbs) {
      return null
    }

    const breadCrumbs = cottagesData.value.breadCrumbs

    if (
      breadCrumbs.province ||
      breadCrumbs.region ||
      breadCrumbs.village ||
      breadCrumbs.zone ||
      breadCrumbs.corner
    ) {
      const breadcrumbItems = decoratorCottageListToMenuBreadcrumb(breadCrumbs)

      if (breadcrumbItems.length > 0) {
        const lastItem = breadcrumbItems[breadcrumbItems.length - 1]
        const middleItems = breadcrumbItems.slice(0, -1)

        return {
          name: lastItem.name,
          middleItems,
        }
      }
    } else {
      if (initialParams.l === 'espana') {
        return {
          name: t('views.cottage-list.results.breadcrumb.cottagesSpain'),
        }
      } else {
        return {
          name: t('views.cottage-list.results.breadcrumb.cottages'),
        }
      }
    }

    return null
  }

  function getTitle(): string {
    if (isFacetsMode && facetsData.value?.title) {
      return t('views.cottage-list.meta.titleFacet', {
        facetTitle: facetsData.value.title,
      })
    }

    const destination = getCurrentLocationName()

    if (isCornerPage.value) {
      return t('views.cottage-list.meta.titleCorner', {
        corner: cottagesData.value!.corner!.name,
      })
    }

    if (hasFiltersApplied()) {
      return t('views.cottage-list.meta.title')
    } else {
      return t('views.cottage-list.meta.titleDestination', {
        destination,
      })
    }
  }

  function getDescription(): string {
    if (isFacetsMode && facetsData.value?.metaDescription) {
      return facetsData.value.metaDescription
    }

    const totalItems = cottagesMeta.value?.totalItems ?? 0

    if (isCornerPage.value) {
      return t('views.cottage-list.meta.descriptionCorner', {
        corner: cottagesData.value!.corner!.name,
      })
    }

    const hasCottages = cottagesData.value?.cottages.length ?? 0
    if (cottagesData.value?.destination) {
      const destination = getCurrentLocationName()
      if (
        ['region', 'province'].includes(cottagesData.value.destination.type)
      ) {
        return t('views.cottage-list.meta.descriptionDestination', {
          destination,
          cottages: totalItems,
        })
      } else if (hasCottages !== 0) {
        return t('views.cottage-list.meta.description', {
          destination,
        })
      }
      return t('views.cottage-list.meta.descriptionNear', {
        destination,
      })
    }
    return t('views.cottage-list.meta.descriptionDestination', {
      destination: t('views.cottage-list.config.default-country'),
      cottages: totalItems,
    })
  }

  function getImage(): HeadInfo['images'] {
    return [
      {
        url:
          cottagesData.value?.destination?.image ??
          '~/assets/img/logo-header.png',
        alt: t('views.cottage-list.meta.title'),
      },
    ]
  }

  function getRobotsVisible(robotsVisible: boolean): HeadInfo['robotsVisible'] {
    if (isFacetsMode && facetsData.value) {
      return facetsData.value.isIndexable
    }

    const isPageVisibleToGoogle = robotsVisible

    if (isPageVisibleToGoogle && cottagesData.value !== undefined) {
      if (
        cottagesData.value.corner !== null &&
        cottagesData.value.corner !== undefined
      ) {
        return cottagesData.value.corner.isIndexable
      } else if (
        cottagesData.value.destination !== null &&
        cottagesData.value.destination !== undefined
      ) {
        return cottagesData.value.destination.isIndexable
      }
    }

    return false
  }

  function getHeadInfoData(robotsVisible = false): HeadInfo {
    return {
      title: getTitle(),
      description: getDescription(),
      images: getImage(),
      robotsVisible: getRobotsVisible(robotsVisible),
    }
  }

  function getCurrentLocationName(): string {
    if (cottagesData.value?.destination) {
      return cottagesData.value.destination.name
    }
    return t('views.cottage-list.config.default-country')
  }

  function showErrorPage(errorObj: ApiResponseError): void {
    throw createError({
      statusCode: errorObj.statusCode,
      message: errorObj.message,
      fatal: true,
    })
  }

  return {
    pending,
    error,
    cottagesData,
    cottagesMeta,
    facetsData,
    fetchCottagesData,
    initializePage,
    getBreadcrumbData,
    getHeadInfoData,
    showErrorPage,
    getCurrentLocationName,
  }
}
